import {useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers'
import {parseResultsSearch} from '../resultsParams'
import {getExperts, PublicExpert} from '../../../services/expertsService'
import {useInfiniteExpertsSearch} from '../../../hooks/search/useInfiniteExpertsSearch'
import {ExpertCardList} from './components/ExpertCardList'

// Experts follow the same 4-column grid as Members, so paginate in multiples of 4 (5 rows × 4)
// to keep the last row filled when OTHERS_LIMIT is exhausted.
const PER_PAGE = 20
// Maximum experts shown under "Other experts" — keeps the section a curated preview.
const OTHERS_LIMIT = 20

const subheadingStyle: React.CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: 20,
  fontWeight: 500,
  color: '#151515',
  margin: '8px 0 18px',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
}

const ExpertsSearchPage = () => {
  const location = useLocation()
  const history = useHistory()
  const [matches, setMatches] = useState<PublicExpert[]>([])
  const [others, setOthers] = useState<PublicExpert[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)

  const {q} = parseResultsSearch(location.search)
  const trimmed = (q ?? '').trim()
  const hasQuery = trimmed.length > 0

  // Modo "discovery": cuando se entra a /expert-search sin query (viene del
  // "See all results" con scope Expert), el listado crece con infinite scroll.
  // Con query se mantiene el flujo matches + preview + paginacion cliente,
  // mismo layout que /members-search.
  const infinite = useInfiniteExpertsSearch({
    enabled: !hasQuery,
    pageSize: PER_PAGE,
  })

  // Click en el titulo "Expert" -> vuelve a /expert-search sin query, lo que
  // limpia el filtro y restablece el modo discovery (infinite scroll).
  const resetToDiscovery = () => {
    if (location.pathname === '/expert-search' && !location.search) {
      history.replace('/expert-search')
    } else {
      history.push('/expert-search')
    }
  }

  useEffect(() => {
    const container = document.getElementById('kt_content_container')
    container?.classList.add('member-full-wrapper')
    return () => container?.classList.remove('member-full-wrapper')
  }, [])

  useEffect(() => {
    // Sin query: el hook de infinite scroll maneja la carga. Limpiamos el
    // estado de matches/others para no mezclar visualmente el listado
    // paginado con el que administra el hook.
    if (!hasQuery) {
      setMatches([])
      setOthers([])
      setPage(1)
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [full, matched] = await Promise.all([
          getExperts('', ''),
          getExperts(trimmed, ''),
        ])
        const matchIdSet = new Set(matched.map((m) => m.id))
        const rest = full.filter((e) => !matchIdSet.has(e.id)).slice(0, OTHERS_LIMIT)

        if (cancelled) return
        setMatches(matched)
        setOthers(rest)
        setPage(1)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, hasQuery])

  const totalPages = Math.max(1, Math.ceil(others.length / PER_PAGE))
  const paginatedOthers = useMemo(
    () => others.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [others, page]
  )
  const goTo = (p: number) => {
    if (p < 1 || p > totalPages) return
    setPage(p)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const hasMatches = matches.length > 0
  const showResultsHeader = hasQuery
  const showOthersHeader = hasQuery && others.length > 0

  return (
    <div className='experts-page members-search-page'>
      <div className='members-page__header' style={{marginTop: 32}}>
        <h1
          className='members-page__title'
          style={{cursor: 'pointer'}}
          role='link'
          tabIndex={0}
          onClick={resetToDiscovery}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              resetToDiscovery()
            }
          }}
        >
          Expert&nbsp;
          <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
        </h1>
      </div>

      {showResultsHeader && (
        <h2 style={subheadingStyle}>Results for &ldquo;{trimmed}&rdquo;</h2>
      )}

      {hasQuery && hasMatches && (
        <ExpertCardList
          data={matches}
          loading={false}
          onItemClick={(id) => history.push(`/experts/${id}`)}
        />
      )}

      {hasQuery && !hasMatches && !loading && (
        <div style={{padding: '32px 0 8px', color: '#666', fontSize: 16, textAlign: 'center'}}>
          No experts match &ldquo;{trimmed}&rdquo;.
        </div>
      )}

      {/* Modo sin query: listado con infinite scroll silencioso. Espejo del
          patron de /members-search. Un sentinel invisible al final del listado
          dispara la carga de la siguiente pagina cuando entra al viewport. */}
      {!hasQuery && (
        <>
          <ExpertCardList
            data={infinite.items}
            loading={infinite.loading && infinite.items.length === 0}
            onItemClick={(id) => history.push(`/experts/${id}`)}
          />
          <div ref={infinite.sentinelRef} aria-hidden='true' style={{height: 1}} />
        </>
      )}

      {/* Modo con query: preview paginada (client-side) bajo "Other experts". */}
      {hasQuery && others.length > 0 && (
        <>
          {showOthersHeader && (
            <h2 style={{...subheadingStyle, marginTop: 32}}>Other experts</h2>
          )}
          <ExpertCardList
            data={paginatedOthers}
            loading={loading && !hasMatches}
            onItemClick={(id) => history.push(`/experts/${id}`)}
          />
        </>
      )}

      {hasQuery && totalPages > 1 && (
        <nav className='members-search-pagination' aria-label='Pagination'>
          <button
            type='button'
            className='members-search-pagination__nav'
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
            aria-label='Previous page'
          >
            ‹
          </button>
          {Array.from({length: totalPages}).map((_, i) => {
            const p = i + 1
            return (
              <button
                key={p}
                type='button'
                className={`members-search-pagination__page ${
                  p === page ? 'members-search-pagination__page--active' : ''
                }`}
                onClick={() => goTo(p)}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            )
          })}
          <button
            type='button'
            className='members-search-pagination__nav'
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
            aria-label='Next page'
          >
            ›
          </button>
        </nav>
      )}
    </div>
  )
}

export {ExpertsSearchPage}
